import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Breadcrumbs,
  Card,
  Chip,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import Link from '@mui/material/Link'
import { Home as HomeIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { LanguageProfileCard } from '@/components/users/LanguageProfileCard'
import { usersApi } from '@/api/users'
import {
  languageProfileApi,
  PROFILE_SKILLS,
  type PlacementRunSummary,
} from '@/api/placement'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import { ROUTES } from '@/utils/constants'
import { formatDateDMY } from '@/utils/date'
import { PROBE_SKILLS, skillMeasurementLabel } from '@/utils/placementLabels'
import '@/styles/dashboard.css'

/**
 * Пройденные тесты уровня одного ученика.
 *
 * Карточка ученика показывает одну цифру — сегодняшний уровень. Учителю этого
 * мало: B1, взятый с первой попытки, и B1 после трёх заходов за полгода —
 * разные ученики, и первый урок им нужен разный.
 *
 * Заданий и ответов здесь нет намеренно. Банк один на всех, пересдача
 * разрешена через месяц — показав разбор конкретных позиций, мы отдали бы
 * ключи к следующему тесту. Вместо разбора — счёт «верных из выданных» по
 * каждому навыку: он объясняет уровень, ничего не выдавая.
 */
export function PlacementRunsPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const userId = Number(id)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [runs, setRuns] = useState<PlacementRunSummary[]>([])
  const [studentName, setStudentName] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      // Профиль страница не грузит: его тянет карточка выше, и второй такой
      // же запрос был бы лишним.
      const [runRows, user] = await Promise.all([
        languageProfileApi.runs(userId),
        usersApi.getById(userId),
      ])

      setRuns(runRows)
      setStudentName(user.display_name ?? user.email ?? '')
    } catch (err) {
      setError(displayErrorMessage(getErrorMessage(err), t('common.error')))
    } finally {
      setLoading(false)
    }
  }, [t, userId])

  useEffect(() => {
    if (!Number.isFinite(userId)) {
      setError(t('common.error'))
      setLoading(false)
      return
    }

    void load()
  }, [load, t, userId])


  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link
            component={RouterLink}
            to={ROUTES.DASHBOARD}
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </Link>
          <Link component={RouterLink} to={ROUTES.USERS} underline="hover">
            {t('dashboard.students')}
          </Link>
          <Link component={RouterLink} to={`/users/${userId}`} underline="hover">
            {studentName || t('profile.title')}
          </Link>
          <Typography color="text.primary">{t('placement.runsTitle')}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h5">{t('placement.runsTitle')}</Typography>
          <PageHelpPopover pageKey="placement-runs" />
        </Box>

        {error !== null && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Уровень, правка учителем и история правок — в карточке профиля:
            она и была тем блоком, который переехал сюда со страницы ученика. */}
        <LanguageProfileCard studentId={userId} canCorrect />

        {loading ? (
          <Skeleton variant="rectangular" height={200} />
        ) : runs.length === 0 ? (
          <Alert severity="info">{t('placement.noRuns')}</Alert>
        ) : (
          <TableContainer component={Card}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('placement.runDate')}</TableCell>
                  <TableCell>{t('placement.runResult')}</TableCell>
                  <TableCell>{t('placement.runSkills')}</TableCell>
                  <TableCell>{t('placement.runScores')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {runs.map((run) => (
                  <TableRow key={run.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                      <Link
                        component={RouterLink}
                        to={ROUTES.USER_PLACEMENT_RUN(userId, run.id)}
                        underline="hover"
                      >
                        {formatDateDMY(run.completed_at ?? run.started_at)}
                      </Link>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t('placement.runBlocks', { count: run.blocks })}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Chip
                        size="small"
                        color={run.status === 'completed' ? 'success' : 'default'}
                        variant={run.status === 'completed' ? 'filled' : 'outlined'}
                        label={t(`placement.runStatus.${run.status}`)}
                      />
                      {run.self_assessed_level != null && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {t('placement.selfAssessedIs', { level: run.self_assessed_level })}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Stack spacing={0.5}>
                        {PROFILE_SKILLS.map((skill) => {
                          const label =
                            run.computed_levels?.[skill]
                            ?? skillMeasurementLabel(run.skill_measurements?.[skill], t, {
                              isProbe: PROBE_SKILLS.includes(skill),
                            })

                          return (
                            <Typography key={skill} variant="body2">
                              {t(`placement.skills.${skill}`)}: {label}
                            </Typography>
                          )
                        })}
                      </Stack>
                    </TableCell>

                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <RunScores run={run} />
                    </TableCell>

                    <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                      <Link
                        component={RouterLink}
                        to={ROUTES.USER_PLACEMENT_RUN(userId, run.id)}
                        underline="hover"
                        variant="body2"
                      >
                        {t('placement.openRun')}
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

      </Box>
    </DashboardLayout>
  )
}

/** Счёт по навыкам одного прогона: верных из выданных. */
function RunScores({ run }: { run: PlacementRunSummary }) {
  const { t } = useTranslation()

  const rows = useMemo(
    () => Object.entries(run.skill_scores ?? {}).filter(([, score]) => score.total > 0),
    [run.skill_scores],
  )

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('placement.noScores')}
      </Typography>
    )
  }

  return (
    <Stack spacing={0.5}>
      {rows.map(([skill, score]) => (
        <Typography key={skill} variant="body2">
          {t(`placement.skills.${skill}`)}: {t('placement.scoreOf', { correct: score.correct, total: score.total })}
        </Typography>
      ))}
    </Stack>
  )
}
